/*
 * (C) 2026 BeyondDemise, All Rights Reserved.
 *
 * BaShell — the post-login application chrome: a `wa-page` with a sidebar nav
 * whose entries come from a static registry, and a main area that mounts the
 * active view's custom element. Adding an admin feature = one registry entry +
 * one view component. Lit light-DOM; no router (event-driven view swap).
 */

import { LitElement, html, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { html as staticHtml, unsafeStatic } from 'lit/static-html.js';
import { BA_LOGOUT_EVENT } from './BaNotAuthorized.js';

/** A registered admin view: nav metadata + the custom-element tag to mount. */
export interface BaView {
  id: string;
  label: string;
  icon: string;
  element: string;
}

/** Static view registry. A0 ships one entry; later sub-projects append. */
export const BA_VIEWS: readonly BaView[] = [
  {
    id: 'customers',
    label: 'Customers',
    icon: 'users',
    element: 'ba-customer-list',
  },
];

@customElement('ba-shell')
export class BaShell extends LitElement {
  @property({ attribute: false }) views: readonly BaView[] = BA_VIEWS;
  @state() activeId: string = BA_VIEWS[0]?.id ?? '';

  override createRenderRoot(): HTMLElement {
    return this;
  }

  private get activeView(): BaView | undefined {
    return this.views.find(view => view.id === this.activeId);
  }

  override render(): TemplateResult {
    return html`
      <wa-page mobile-breakpoint="868" navigation-placement="start">
        <header slot="header" class="wa-split">
          <strong>BeyondDemise Admin</strong>
          <wa-button
            size="s"
            variant="neutral"
            data-testid="logout"
            @click=${this.onLogout}
          >
            Log out
          </wa-button>
        </header>
        <nav slot="navigation">
          ${this.views.map(
            view => html`
              <wa-button
                appearance=${view.id === this.activeId ? 'accent' : 'plain'}
                data-view=${view.id}
                @click=${() => this.showView(view.id)}
              >
                <wa-icon slot="start" name=${view.icon}></wa-icon>
                ${view.label}
              </wa-button>
            `
          )}
        </nav>
        <main id="ba-main">${this.renderActive()}</main>
      </wa-page>
    `;
  }

  private renderActive(): TemplateResult {
    const view = this.activeView;
    if (!view) {
      return html`<wa-callout variant="neutral">No view selected.</wa-callout>`;
    }
    const tag = unsafeStatic(view.element);
    return staticHtml`<${tag}></${tag}>`;
  }

  /** Switch the active view. */
  showView(id: string): void {
    this.activeId = id;
  }

  private onLogout(): void {
    this.dispatchEvent(
      new CustomEvent(BA_LOGOUT_EVENT, { bubbles: true, composed: true })
    );
  }
}
